import type { MembershipCategory } from "./types"

import { membershipCategoryConfigurationMap, defaultMembershipCategoryKey } from "./constants"
import {
  inferRequirementStep,
  isDocumentRequirement,
  normaliseRequirement,
  type ResolvedRequirement,
} from "./requirements"
import type {
  MembershipCategoryConfiguration,
  MembershipCategoryKey,
  MembershipCategorySectionConfig,
  StepRequirementSummary,
} from "./types"

const CATEGORY_KEYWORDS: Array<{ key: MembershipCategoryKey; matches: string[] }> = [
  { key: "student", matches: ["student"] },
  { key: "professional", matches: ["professional", "individual"] },
  { key: "corporate", matches: ["corporate", "organisation", "organization", "company"] },
]

export function normaliseMembershipCategoryKey(name?: string | null): MembershipCategoryKey {
  const formatted = name?.trim().toLowerCase()
  if (!formatted) {
    return defaultMembershipCategoryKey
  }

  for (const { key, matches } of CATEGORY_KEYWORDS) {
    if (matches.some((match) => formatted.includes(match))) {
      return key
    }
  }

  return defaultMembershipCategoryKey
}

function cloneConfiguration(
  configuration: MembershipCategoryConfiguration,
): MembershipCategoryConfiguration {
  return {
    ...configuration,
    prerequisites: [...configuration.prerequisites],
    supportingDocuments: configuration.supportingDocuments
      ? [...configuration.supportingDocuments]
      : undefined,
    sections: Object.entries(configuration.sections).reduce<
      MembershipCategoryConfiguration['sections']
    >((accumulator, [stepId, section]) => {
      accumulator[stepId as keyof MembershipCategoryConfiguration['sections']] = {
        ...section,
        requiredFields: section.requiredFields ? [...section.requiredFields] : undefined,
        optionalFields: section.optionalFields ? [...section.optionalFields] : undefined,
        requirementSummaries: section.requirementSummaries
          ? [...section.requirementSummaries]
          : undefined,
        documentRequirements: section.documentRequirements
          ? [...section.documentRequirements]
          : undefined,
      }
      return accumulator
    }, {} as MembershipCategoryConfiguration['sections']),
  }
}

const OPTIONAL_STEPS: Array<MembershipCategorySectionConfig['stepId']> = [
  'employment',
  'education',
  'affiliations',
  'documents',
]

function createRequirementSummary(
  requirement: ResolvedRequirement,
): StepRequirementSummary {
  return {
    id: requirement.id,
    label: requirement.label,
    mandatory: requirement.mandatory,
    description: requirement.description,
    type: requirement.type,
    acceptedFormats:
      requirement.metadata?.acceptedFormats && Array.isArray(requirement.metadata.acceptedFormats)
        ? requirement.metadata.acceptedFormats.map((format) => String(format))
        : undefined,
  }
}

function mergeDocumentSupportingList(
  base?: string[],
  additions?: StepRequirementSummary[],
) {
  const merged = new Map<string, string>()
  if (Array.isArray(base)) {
    for (const item of base) {
      if (typeof item === 'string' && item.trim().length > 0) {
        merged.set(item.toLowerCase(), item)
      }
    }
  }

  if (Array.isArray(additions)) {
    for (const requirement of additions) {
      const label = requirement.label.trim()
      if (label) {
        const key = label.toLowerCase()
        let value = label
        if (requirement.acceptedFormats && requirement.acceptedFormats.length > 0) {
          value = `${label} (${requirement.acceptedFormats.join(', ')})`
        }
        merged.set(key, value)
      }
    }
  }

  return Array.from(merged.values())
}

export function resolveMembershipCategoryConfiguration(
  category?: MembershipCategory | null,
): MembershipCategoryConfiguration {
  const key = normaliseMembershipCategoryKey(category?.categoryName)
  const baseConfiguration =
    membershipCategoryConfigurationMap[key]
    ?? membershipCategoryConfigurationMap[defaultMembershipCategoryKey]

  const configuration = cloneConfiguration(baseConfiguration)
  const rawRequirements = Array.isArray(category?.requirements)
    ? category?.requirements
    : []

  const resolvedRequirements = rawRequirements
    .map((requirement) => normaliseRequirement(requirement))
    .filter((requirement): requirement is ResolvedRequirement => Boolean(requirement))

  if (resolvedRequirements.length === 0) {
    return configuration
  }

  const requirementSummariesByStep = new Map<
    MembershipCategorySectionConfig['stepId'],
    StepRequirementSummary[]
  >()
  const documentRequirementSummaries: StepRequirementSummary[] = []

  for (const requirement of resolvedRequirements) {
    const stepId = inferRequirementStep(requirement)
    const section = configuration.sections[stepId]
    if (!section) continue

    if (section.isVisible === false) {
      section.isVisible = true
    }

    if (requirement.mandatory && section.isRequired === false) {
      section.isRequired = true
    }

    const summary = createRequirementSummary(requirement)

    if (isDocumentRequirement(requirement)) {
      documentRequirementSummaries.push(summary)
      const existingDocuments = requirementSummariesByStep.get('documents') ?? []
      existingDocuments.push(summary)
      requirementSummariesByStep.set('documents', existingDocuments)
    } else {
      const existing = requirementSummariesByStep.get(stepId) ?? []
      existing.push(summary)
      requirementSummariesByStep.set(stepId, existing)
    }
  }

  for (const [stepId, summaries] of requirementSummariesByStep.entries()) {
    const section = configuration.sections[stepId]
    if (!section) continue

    const sortedSummaries = summaries.slice().sort((a, b) => {
      if (a.mandatory === b.mandatory) {
        return a.label.localeCompare(b.label)
      }
      return a.mandatory ? -1 : 1
    })

    if (stepId === 'documents') {
      section.documentRequirements = sortedSummaries
    } else {
      section.requirementSummaries = sortedSummaries
    }
  }

  if (documentRequirementSummaries.length > 0) {
    configuration.supportingDocuments = mergeDocumentSupportingList(
      configuration.supportingDocuments,
      documentRequirementSummaries,
    )
  }

  for (const section of Object.values(configuration.sections)) {
    if (!OPTIONAL_STEPS.includes(section.stepId)) {
      continue
    }

    const hasRequirements = Boolean(
      (section.stepId === 'documents'
        ? section.documentRequirements && section.documentRequirements.length > 0
        : section.requirementSummaries && section.requirementSummaries.length > 0),
    )

    if (!hasRequirements && section.isRequired === false) {
      if (section.stepId === 'documents') {
        const hasSupportingDocs = Array.isArray(configuration.supportingDocuments)
          && configuration.supportingDocuments.length > 0
        if (!hasSupportingDocs) {
          section.isVisible = false
        }
      } else {
        section.isVisible = false
      }
    }
  }

  // Hide requirements step if there are no non-document requirements
  const requirementsSection = configuration.sections.requirements
  if (requirementsSection) {
    const hasNonDocumentRequirements = resolvedRequirements.some(
      (req) => !isDocumentRequirement(req)
    )
    if (!hasNonDocumentRequirements) {
      requirementsSection.isVisible = false
    }
  }

  return configuration
}
